import {Button, FormControl, FormField, IconButton, Tooltip} from 'platform/components';
import {Grid, HStack, VStack} from 'platform/foundation';

import {
  ArrayPath,
  FieldArray,
  FieldValues,
  Path,
  PathValue,
  useFieldArray,
  UseFormReturn,
} from 'react-hook-form';

import {findIndex, isNil, last, isNotEmpty} from 'ramda';
import {isNotNil, lengthLte} from 'ramda-adjunct';

import {BaseSupplier, WarehouseSupplier} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {getOptionsFromSuppliers} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

type SupplierFieldListProps<TFieldValues extends FieldValues = FieldValues> = {
  control: FormControl<TFieldValues>;
  formApi: UseFormReturn<TFieldValues>;
  name: Path<TFieldValues>;
  isRequired?: boolean;
  isDisabled?: boolean;
  suppliers: BaseSupplier[] | Nullish;
} & TestIdProps;

const MINIMUM_NUMBER_OF_SUPPLIERS = 1;
// First supplier in array is required
const INDEX_OF_REQUIRED_SUPPLIER = 0;

const EMPTY_SUPPLIER = {id: null};

export function SupplierFieldList<TFieldValues extends FieldValues = FieldValues>(
  props: SupplierFieldListProps<TFieldValues>
) {
  const {fields, remove, append} = useFieldArray({
    control: props.control,
    name: props.name as ArrayPath<TFieldValues>,
  });

  const defaultSupplierId = props.formApi.watch('defaultSupplierId' as Path<TFieldValues>);

  const selectedSuppliers: WarehouseSupplier[] =
    props.formApi.watch('suppliers' as Path<TFieldValues>) ?? [];

  const defaultFields = isNotEmpty(fields) ? fields : [null];

  const indexOfDefaultValue = findIndex(
    (field: WarehouseSupplier) => field?.id === defaultSupplierId
  )(selectedSuppliers);

  const isLastSupplierUndefined = isNil(last(selectedSuppliers)?.id);
  const hasAtLeastOneSupplier = lengthLte(MINIMUM_NUMBER_OF_SUPPLIERS, fields);

  const suppliersErrorMessage = props.formApi.formState.errors[props.name]?.message;

  const handleSelectDefault = (newDefaultIndex: number) => {
    const newDefaultValue: WarehouseSupplier = props.formApi.getValues(
      'suppliers' as Path<TFieldValues>
    )[newDefaultIndex];

    if (isNil(newDefaultValue)) {
      return;
    }

    props.formApi.setValue(
      'defaultSupplierId' as Path<TFieldValues>,
      newDefaultValue.id as PathValue<TFieldValues, Path<TFieldValues>>
    );
  };

  const handleSupplierChange = (supplierId: PathValue<TFieldValues, Path<TFieldValues>>) => {
    const hasSupplierByDefaultSupplierId = selectedSuppliers.find(
      (supplier) => supplier.id === defaultSupplierId
    );

    if (!hasSupplierByDefaultSupplierId) {
      props.formApi.setValue('defaultSupplierId' as Path<TFieldValues>, supplierId);
      props.formApi.trigger();
    }
  };

  return (
    <VStack spacing={4} data-testid={`${props['data-testid']}-wrapper`}>
      {defaultFields?.map((field, index) => (
        <Grid key={field?.id} columns={8}>
          <FormField
            isRequired={props.isRequired && index === INDEX_OF_REQUIRED_SUPPLIER}
            isNotClearable
            control={props.control}
            type="choice"
            errorMessage={suppliersErrorMessage as string}
            name={`${props.name}.${index}.id` as Path<TFieldValues>}
            label={isFirstItem(index) ? i18n.t('entity.warehouse.labels.supplier') : undefined}
            options={getOptionsFromSuppliers(
              getAvailableSuppliersForIndex(props.suppliers, selectedSuppliers, index)
            )}
            onChange={(value) =>
              handleSupplierChange(value as PathValue<TFieldValues, Path<TFieldValues>>)
            }
            data-testid={suffixTestId(`supplier-[${index}]`, props)}
          />

          <HStack>
            <HStack spacing={4} align="flex-end">
              <Tooltip
                isDisabled={index !== indexOfDefaultValue}
                description={i18n.t('entity.warehouse.labels.cannotRemoveDefaultSupplier')}
              >
                <IconButton
                  icon="action/delete"
                  isDisabled={hasAtLeastOneSupplier || index === indexOfDefaultValue}
                  severity="danger"
                  onClick={() => remove(index)}
                  data-testid={suffixTestId(`remove-[${index}]`, props)}
                />
              </Tooltip>
            </HStack>

            <HStack spacing={4} align="flex-end">
              <Tooltip
                isDisabled={isNotNil(selectedSuppliers[index]?.id)}
                description={i18n.t('general.actions.firstlyFulfillSupplierInLastField')}
              >
                <IconButton
                  icon={
                    indexOfDefaultValue === index
                      ? 'toggle/radio_button_checked'
                      : 'toggle/radio_button_unchecked'
                  }
                  isDisabled={isNil(selectedSuppliers[index]?.id)}
                  severity="informational"
                  onClick={() => handleSelectDefault(index)}
                  data-testid={suffixTestId(`setDefault-[${index}]`, props)}
                />
              </Tooltip>
            </HStack>
          </HStack>
        </Grid>
      ))}

      <HStack>
        <Tooltip
          isDisabled={!isLastSupplierUndefined}
          description={i18n.t('general.actions.firstlyFulfillSupplierInLastField')}
          placement="right"
        >
          <Button
            isDisabled={isLastSupplierUndefined}
            leftIcon="content/add_circle"
            title={i18n.t('general.actions.addAnother')}
            size="small"
            variant="link"
            onClick={() =>
              append(EMPTY_SUPPLIER as FieldArray<TFieldValues, ArrayPath<TFieldValues>>)
            }
            data-testid={suffixTestId('addAnother', props)}
          />
        </Tooltip>
      </HStack>
    </VStack>
  );
}

const isFirstItem = (index: number) => index === 0;

const getAvailableSuppliersForIndex = (
  suppliers: BaseSupplier[] | Nullish,
  selectedSuppliers: WarehouseSupplier[],
  currentIndex: number
): BaseSupplier[] => {
  if (isNil(suppliers)) {
    return [];
  }

  const otherSelectedIds = selectedSuppliers
    .filter((_, index) => index !== currentIndex)
    .map((supplier) => supplier.id)
    .filter(isNotNil);

  return suppliers.filter((supplier) => !otherSelectedIds.includes(supplier.id));
};
